import React from 'react'
import gql from 'graphql-tag'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { Query, Mutation } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { logError, logSuccess } from '../../toaster'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { excludeFrom, TagDialog } from '../../../legacy-design-system/tags'

const availableTagsQuery = gql`
  query AvailableServerTags($organizationID: String!) {
    organizationServerTags(organizationID: $organizationID) {
      tags
    }
  }
`

interface ModifyTagProps {
  add: string[]
  remove: string[]
}

interface DialogProps {
  isOpen: boolean
  closeDialog: () => void
  initialTags: string[]
  organizationID: string
}

interface MutationProps {
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  serverIDs: string[]
}

function ModifyServerTags({
  isOpen,
  closeDialog,
  initialTags,
  organizationID,
  modifyTags,
  multipleServers
}: DialogProps & {
  multipleServers: boolean
  modifyTags: (props: ModifyTagProps) => Promise<any>
}) {
  return (
    <Query query={availableTagsQuery} variables={{ organizationID }}>
      {(result) => {
        const { data, error, loading, refetch } = result
        const availableTags =
          error || loading ? [] : data.organizationServerTags.tags

        return (
          <ContrastFormattedMessage
            id={
              multipleServers
                ? 'title_servers_tagServers'
                : 'title_serverDashboard_tagServer'
            }
          >
            {(title) => (
              <ContrastFormattedMessage
                id="plural_servers_successfullyTaggedServer"
                values={{ count: multipleServers ? 2 : 1 }}
              >
                {(successMessage) => (
                  <ContrastFormattedMessage
                    id="plural_servers_errorTaggingServer"
                    values={{ count: multipleServers ? 2 : 1 }}
                  >
                    {(errorMessage) => (
                      <TagDialog
                        initialAvailableTags={availableTags}
                        initialSelectedTags={initialTags}
                        onConfirm={(selectedTags) => {
                          const add = excludeFrom(selectedTags, initialTags)
                          const remove = excludeFrom(initialTags, selectedTags)

                          if (add.length + remove.length > 0) {
                            modifyTags({ add, remove })
                              .then(() => {
                                logSuccess(successMessage)
                                refetch()
                              })
                              .catch((error) => {
                                logError(errorMessage)
                                return error
                              })
                          }

                          closeDialog()
                        }}
                        onCancel={closeDialog}
                        title={title}
                        isOpen={isOpen}
                      />
                    )}
                  </ContrastFormattedMessage>
                )}
              </ContrastFormattedMessage>
            )}
          </ContrastFormattedMessage>
        )
      }}
    </Query>
  )
}

const serverTagsFragment = gql`
  fragment server on Server {
    tags
  }
`

function updateTags(serverIDs: string[], add: string[], remove: string[]) {
  return (proxy) => {
    serverIDs.forEach((serverID) => {
      const id =
        defaultDataIdFromObject({
          __typename: 'Server',
          id: serverID
        }) || ''

      const { tags }: any = proxy.readFragment({
        id,
        fragment: serverTagsFragment
      })

      proxy.writeFragment({
        id,
        fragment: serverTagsFragment,
        data: {
          tags: tags.filter((t) => remove.every((r) => r !== t)).concat(add),
          __typename: 'Server'
        }
      })
    })
  }
}

function getOptimisticResponse(fieldName) {
  return {
    [fieldName]: {
      _placeholder: true,
      __typename: 'ServerBulkResponse'
    },
    __typename: 'Mutation'
  }
}

class ModifyTagsMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    ids: string[]
  }
> {}

const modifyServersTagsMutationName = 'modifyServersTags'
const modifyServersTagsMutation = gql`
  mutation ${modifyServersTagsMutationName}(
    $organizationID: ID!
    $add: [ID!]
    $remove: [ID!]
    $ids: [ID!]!
  ) {
    ${modifyServersTagsMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        ids: $ids
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyServerTagsDialog({
  organizationID,
  serverIDs,
  refetchQueries,
  refresh,
  initialTags,
  closeDialog,
  isOpen
}: DialogProps & MutationProps) {
  return (
    <ModifyTagsMutation mutation={modifyServersTagsMutation} ignoreResults>
      {(mutate) => {
        return (
          <ModifyServerTags
            isOpen={isOpen}
            initialTags={initialTags}
            closeDialog={closeDialog}
            multipleServers={serverIDs.length > 1}
            organizationID={organizationID}
            modifyTags={({ add, remove }) => {
              return mutate({
                refetchQueries,
                variables: { add, remove, organizationID, ids: serverIDs },
                optimisticResponse: getOptimisticResponse(
                  modifyServersTagsMutationName
                ),
                update: updateTags(serverIDs, add, remove)
              }).then(refresh)
            }}
          />
        )
      }}
    </ModifyTagsMutation>
  )
}

class ModifyTagsByFilterMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    excludedServers: string[]
    filters: any
  }
> {}

const modifyServerTagsByFilterMutationName = 'modifyServerTagsByFilters'
const modifyServerTagsByFilterMutation = gql`
  mutation ${modifyServerTagsByFilterMutationName}(
    $organizationID: ID!
    $add: [String!]!
    $remove: [String!]!
    $filters: ServerFilterArg!
    $excludedServers: [String!]!
  ) {
    ${modifyServerTagsByFilterMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        filters: $filters
        excludedServers: $excludedServers
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyServerTagsByFilterDialog({
  filters,
  organizationID,
  excludedServers,
  refresh,
  refetchQueries,
  initialTags,
  closeDialog,
  isOpen,
  serverIDs
}: DialogProps &
  MutationProps & {
    organizationID: string
    excludedServers: string[]
    filters: any
  }) {
  return (
    <ModifyTagsByFilterMutation mutation={modifyServerTagsByFilterMutation}>
      {(mutate) => {
        return (
          <ModifyServerTags
            initialTags={initialTags}
            closeDialog={closeDialog}
            isOpen={isOpen}
            multipleServers={true}
            organizationID={organizationID}
            modifyTags={({ add, remove }) =>
              mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  filters,
                  organizationID,
                  excludedServers
                },
                optimisticResponse: getOptimisticResponse(
                  modifyServerTagsByFilterMutationName
                ),
                update: updateTags(serverIDs, add, remove)
              }).then(refresh)
            }
          />
        )
      }}
    </ModifyTagsByFilterMutation>
  )
}
